import React, { useEffect, useState } from 'react';
import CoachLayout from './CoachLayout';

const API_BASE = process.env.REACT_APP_SCENARIO_OS_API || '/scenario-os';

function authHeaders() {
  const token = localStorage.getItem('scenario_os_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch(path) {
  const res = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

async function apiPost(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}

function roleMeta(role) {
  if (role === 'learner') return { label: 'Learner', color: '#1565c0', align: 'flex-end' };
  if (role === 'coach_direct') return { label: '👤 Coach (direct)', color: '#2e7d32', align: 'flex-start' };
  if (role === 'coach_agent') return { label: '🤖 Coach (agent)', color: '#6a1b9a', align: 'flex-start' };
  return { label: '🤖 Coach AI', color: '#555', align: 'flex-start' };
}

function MessageThread({ messages }) {
  if (!messages || messages.length === 0) {
    return <p style={{ color: '#999', fontStyle: 'italic', fontSize: 13 }}>No messages yet.</p>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {messages.map((msg, i) => {
        const { label, color, align } = roleMeta(msg.role);
        return (
          <div key={i} style={{ display: 'flex', justifyContent: align }}>
            <div style={{
              maxWidth: '75%',
              background: align === 'flex-end' ? '#e3f2fd' : '#f5f5f5',
              borderRadius: 10,
              padding: '8px 12px',
              fontSize: 13,
            }}>
              <div style={{ fontSize: 11, color, fontWeight: 600, marginBottom: 2 }}>{label}</div>
              <div style={{ wordBreak: 'break-word', lineHeight: 1.45 }}>{msg.content}</div>
              {msg.timestamp && (
                <div style={{ fontSize: 10, color: '#aaa', marginTop: 3, textAlign: 'right' }}>
                  {new Date(msg.timestamp).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReplyBox({ sessionId, onReplySent }) {
  const [text, setText] = useState('');
  const [useAgent, setUseAgent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  async function sendReply() {
    if (!text.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      await apiPost('/chat/coach/reply', {
        session_id: sessionId,
        message: text.trim(),
        use_agent: useAgent,
      });
      setText('');
      if (onReplySent) onReplySent();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{
      marginTop: 12,
      padding: 12,
      background: '#f9f9f9',
      borderRadius: 8,
      border: '1px solid #ddd',
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#333' }}>
        Reply to Learner
      </div>
      <textarea
        style={{
          width: '100%',
          border: '1px solid #ccc',
          borderRadius: 6,
          padding: '7px 10px',
          fontSize: 13,
          resize: 'vertical',
          minHeight: 64,
          fontFamily: 'inherit',
          boxSizing: 'border-box',
        }}
        placeholder="Type a direct message or use the AI agent…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={sending}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <label style={{ fontSize: 13, color: '#555', display: 'flex', alignItems: 'center', gap: 5 }}>
          <input
            type="checkbox"
            checked={useAgent}
            onChange={(e) => setUseAgent(e.target.checked)}
            disabled={sending}
          />
          Use AI agent
        </label>
        <button
          onClick={sendReply}
          disabled={sending || !text.trim()}
          style={{
            background: '#1565c0',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '7px 18px',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            opacity: sending || !text.trim() ? 0.6 : 1,
          }}
        >
          {sending ? 'Sending…' : 'Send Reply'}
        </button>
        {error && <span style={{ color: '#c62828', fontSize: 12 }}>{error}</span>}
      </div>
    </div>
  );
}

function SessionRow({ session, expanded, onToggle, onReloadSession }) {
  const [fullSession, setFullSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [showReply, setShowReply] = useState(false);

  async function loadFull() {
    if (fullSession && !onReloadSession) return;
    setLoadingSession(true);
    try {
      const data = await apiFetch(`/chat/sessions/${session.session_id}`);
      setFullSession(data);
    } catch (err) {
      console.error('Failed to load session:', err);
    } finally {
      setLoadingSession(false);
    }
  }

  useEffect(() => {
    if (expanded) loadFull();
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleReplySent() {
    loadFull();
    setShowReply(false);
  }

  return (
    <div style={{
      borderBottom: '1px solid #e0e0e0',
      background: expanded ? '#fafafa' : '#fff',
    }}>
      {/* Row header — clickable to expand */}
      <div
        onClick={onToggle}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 100px 1fr 60px 140px',
          gap: 12,
          padding: '12px 16px',
          cursor: 'pointer',
          alignItems: 'center',
          fontSize: 13,
        }}
      >
        <div style={{ fontWeight: 500 }}>{session.learner_name || session.learner_id}</div>
        <div style={{
          background: '#e3f2fd',
          color: '#1565c0',
          borderRadius: 12,
          padding: '2px 8px',
          fontSize: 12,
          textAlign: 'center',
        }}>
          {session.domain}
        </div>
        <div style={{ color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {session.last_message_preview || '—'}
        </div>
        <div style={{ textAlign: 'center', color: '#777' }}>{session.message_count}</div>
        <div style={{ color: '#999', fontSize: 12 }}>
          {session.updated_at ? new Date(session.updated_at).toLocaleString() : '—'}
        </div>
      </div>

      {/* Expanded thread */}
      {expanded && (
        <div style={{ padding: '0 16px 16px' }}>
          {loadingSession ? (
            <p style={{ color: '#888', fontSize: 13 }}>Loading messages…</p>
          ) : (
            <>
              <MessageThread messages={fullSession?.messages} />
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setShowReply((v) => !v)}
                  style={{
                    background: showReply ? '#eee' : '#1565c0',
                    color: showReply ? '#333' : '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '6px 14px',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {showReply ? 'Cancel' : 'Reply'}
                </button>
              </div>
              {showReply && (
                <ReplyBox sessionId={session.session_id} onReplySent={handleReplySent} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function ChatMonitorPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('');

  async function loadSessions() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/chat/coach/all-sessions');
      setSessions(data.sessions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadSessions(); }, []);

  const filtered = sessions.filter((s) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (s.learner_name || '').toLowerCase().includes(q) ||
      (s.domain || '').toLowerCase().includes(q) ||
      (s.last_message_preview || '').toLowerCase().includes(q)
    );
  });

  return (
    <CoachLayout title="Learner Chat Monitor">
      <div style={{ maxWidth: 1100 }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Filter by learner, domain, or message…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              flex: 1,
              border: '1px solid #ccc',
              borderRadius: 6,
              padding: '7px 12px',
              fontSize: 13,
              maxWidth: 360,
            }}
          />
          <button
            onClick={loadSessions}
            style={{
              background: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: 6,
              padding: '7px 14px',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Refresh
          </button>
          <span style={{ fontSize: 13, color: '#666' }}>
            {sessions.length} session{sessions.length !== 1 ? 's' : ''}
          </span>
        </div>

        {error && (
          <div style={{ color: '#c62828', background: '#ffebee', padding: 12, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            Error: {error}
          </div>
        )}

        {loading ? (
          <p style={{ color: '#888' }}>Loading sessions…</p>
        ) : (
          <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
            {/* Table header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 100px 1fr 60px 140px',
              gap: 12,
              padding: '10px 16px',
              background: '#f5f5f5',
              borderBottom: '2px solid #e0e0e0',
              fontSize: 12,
              fontWeight: 700,
              color: '#555',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              <div>Learner</div>
              <div>Domain</div>
              <div>Last Message</div>
              <div style={{ textAlign: 'center' }}># Msgs</div>
              <div>Updated</div>
            </div>

            {filtered.length === 0 && (
              <div style={{ padding: 32, textAlign: 'center', color: '#999', fontSize: 14 }}>
                {sessions.length === 0 ? 'No chat sessions yet.' : 'No sessions match your filter.'}
              </div>
            )}

            {filtered.map((session) => (
              <SessionRow
                key={session.session_id}
                session={session}
                expanded={expandedId === session.session_id}
                onToggle={() =>
                  setExpandedId((prev) => (prev === session.session_id ? null : session.session_id))
                }
                onReloadSession={expandedId === session.session_id}
              />
            ))}
          </div>
        )}
      </div>
    </CoachLayout>
  );
}
